// src/main.js
import { createApp, reactive } from "vue";
import App from "./App.vue";

// 🔐 Firebase Auth (já inicializado em firebase.js)
import { auth } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";

import router from "./router";
import VueSidebarMenu from "vue-sidebar-menu";
import "vue-sidebar-menu/dist/vue-sidebar-menu.css";
import "@fortawesome/fontawesome-free/css/all.min.css";

import { handleAuthError } from "@/utils/sessionUtils";

const apiBaseUrl =
  process.env.VUE_APP_API_BASE_URL || "https://177.36.4.221:3000";

/**
 * Tenta extrair o e-mail de um JWT (SSO)
 */
function extractEmailFromToken(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );

    const payload = JSON.parse(jsonPayload);

    // tenta vários campos comuns
    return (
      payload.email ||
      payload.upn ||
      payload.unique_name ||
      payload.preferred_username ||
      null
    );
  } catch (e) {
    console.warn("⚠️ Não foi possível extrair e-mail do token SSO:", e);
    return null;
  }
}

// 🔑 Captura token vindo do SSO (mag-auth.html?token=...)
// (repare que agora o e-mail pode vir do token, não só da query string)
const url = new URL(window.location.href);
const tokenFromUrl = url.searchParams.get("token");
const emailFromUrl = url.searchParams.get("email");

if (tokenFromUrl) {
  // guarda o token para o secureFetch / backend
  localStorage.setItem("userToken", tokenFromUrl);

  // 👉 NOVO: tenta descobrir o e-mail
  let effectiveEmail = emailFromUrl;

  if (!effectiveEmail) {
    effectiveEmail = extractEmailFromToken(tokenFromUrl);
  }

  if (effectiveEmail) {
    localStorage.setItem("magToken", tokenFromUrl);
    localStorage.setItem("email_ini", effectiveEmail);
    localStorage.setItem("ssoEmail", effectiveEmail);
  } else {
    console.warn(
      "⚠️ Token SSO sem e-mail nos claims. A UI pode mostrar só que está autenticado."
    );
  }

  // opcional: já registra a sessão no backend
  fetch(
    (process.env.VUE_APP_API_BASE_URL ||
      "") + "/store-session",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tokenFromUrl}`,
      },
      body: JSON.stringify({
        email: effectiveEmail || "from-id-token-later-se-tiver-claims",
      }),
    }
  ).catch((e) => console.warn("Erro ao chamar /store-session:", e));

  // remove o token da URL, deixando a rota limpa
  url.searchParams.delete("token");
  url.searchParams.delete("email");
  window.history.replaceState({}, "", url.toString());

  const acessoComWhatsapp =
    localStorage.getItem("acessoComWhatsapp") === "true";

  if (acessoComWhatsapp) {
    // Se login foi marcado como "Acesso com WhatsApp", manda para o QR Code
    router.replace("/qr-code");
  } else {
    // Caso contrário, volta para a última página ou pesquisa-cnpj
    const lastPage =
      localStorage.getItem("currentPage") || "/pesquisa-cnpj";
    router.replace(lastPage);
  }

}

// 🔄 Depois de processar o token, lê do localStorage
const storedEmailInit =
  localStorage.getItem("email_ini") || localStorage.getItem("ssoEmail");

// 🔁 Estado global simples para o app
export const authState = reactive({
  isAuthenticated: !!(storedEmailInit || localStorage.getItem("userToken")),
  userEmail: storedEmailInit || null,
});

// 🔧 Cria instância do app Vue (AGORA SIM, ANTES DE USAR app.config)
const app = createApp(App);
window.__app__ = app;

app.use(router);
app.use(VueSidebarMenu);

// disponibiliza o estado global
app.provide("authState", authState);

// props globais
app.config.globalProperties.$authState = authState;
app.config.globalProperties.$isAuthenticated = authState.isAuthenticated;
app.config.globalProperties.$userEmail = authState.userEmail;

/**
 * 🔑 Resolve o token a ser enviado para o backend
 * - Se tiver usuário Firebase logado, usa o ID token
 * - Senão, usa o token SSO salvo em localStorage (userToken)
 */
async function resolveApiToken() {
  try {
    if (auth.currentUser) {
      return await auth.currentUser.getIdToken(true);
    }
  } catch (e) {
    console.warn("⚠️ Erro ao tentar ler usuário do Firebase:", e);
  }

  const ssoToken = localStorage.getItem("userToken");
  if (ssoToken) return ssoToken;

  return null;
}

/**
 * secureFetch — centraliza envio do Authorization (Firebase ou SSO)
 */
export async function secureFetch(url, options = {}) {
  try {
    const token = await resolveApiToken();

    console.log(`URL: ${url}`);
    console.log(`TOKEN: ${token}`);

    const headers = {
      ...(options.headers || {}),
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const res = await fetch(url, {
      ...options,
      headers,
    });

    if (res.status === 401) {
      let body;
      try {
        body = await res.clone().json();
      } catch (e) {
        console.warn("⚠️ Resposta 401 sem JSON válido.");
      }

      if (
        body?.message?.includes("expirado") ||
        body?.message?.includes("Token inválido")
      ) {
        console.warn("⚠️ Token expirado detectado. Deslogando...");
        alert("Sua sessão expirou. Faça login novamente.");

        localStorage.setItem("logout", Date.now());
        await signOut(auth);
        localStorage.clear();
        router.push("/");
        return null;
      }
    }

    return res;
  } catch (err) {
    console.error("❌ Erro no secureFetch:", err);

    if (err instanceof TypeError && err.message === "Failed to fetch") {
      console.warn("⚠️ Falha de rede ao tentar acessar:", url);
      return null;
    }

    throw err;
  }
}

/**
 * 🔁 Força logout em outras abas abertas
 */
window.addEventListener("storage", (event) => {
  if (event.key === "logout") {
    console.warn("📴 Logout sincronizado detectado em outra aba.");
    signOut(auth);
    router.push("/");
  }
});

/**
 * ⏳ Espera o Firebase restaurar a sessão ANTES de montar o app.
 */
new Promise((resolve) => {
  onAuthStateChanged(auth, async (user) => {
    if (user) {
      const email = user.email || authState.userEmail;

      authState.isAuthenticated = true;
      authState.userEmail = email;

      app.config.globalProperties.$isAuthenticated = true;
      app.config.globalProperties.$userEmail = email;

      if (email) {
        localStorage.setItem("email_ini", email);
      }
    } else {
      const storedEmail =
        localStorage.getItem("email_ini") ||
        localStorage.getItem("ssoEmail");

      if (storedEmail) {
        authState.isAuthenticated = true;
        authState.userEmail = storedEmail;
      } else {
        authState.isAuthenticated = false;
        authState.userEmail = null;
      }

      app.config.globalProperties.$isAuthenticated =
        authState.isAuthenticated;
      app.config.globalProperties.$userEmail = authState.userEmail;
    }

    try {
      const ssoInFlight =
        sessionStorage.getItem("ssoRedirectInFlight") === "1";

      if (user && !ssoInFlight) {
        await secureFetch(`${apiBaseUrl}/check-session`, {
          method: "GET",
        });
      }
    } catch (error) {
      console.warn("🔴 Erro ao verificar sessão inicial:", error);
      await handleAuthError(router);
    } finally {
      resolve();
    }
  });
}).finally(() => {
  app.mount("#app");
});
